import axios from "axios";
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet-async";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const SITE = process.env.REACT_APP_BACKEND_URL;

export const http = axios.create({ baseURL: API, withCredentials: true });

export const isDev = process.env.NODE_ENV !== "production";
export function logError(...args) {
  // Dev-only logging; swap for Sentry/LogRocket in production if desired.
  if (isDev) console.error(...args);
}

export function fmtErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data.user);
    } catch (e) {
      if (e?.response?.status !== 401) logError("Session check failed:", e);
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await http.post("/auth/login", { email, password });
    setUser(data.user); return data.user;
  }, []);
  const register = useCallback(async (email, password, name) => {
    const { data } = await http.post("/auth/register", { email, password, name });
    setUser(data.user); return data.user;
  }, []);
  const logout = useCallback(async () => { await http.post("/auth/logout"); setUser(false); }, []);

  const value = useMemo(
    () => ({ user, ready, setUser, login, register, logout, refresh }),
    [user, ready, login, register, logout, refresh]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

// SEO helper with JSON-LD support
export function Seo({ title, description, path = "", image, type = "website", jsonLd }) {
  const url = `${SITE}${path}`;
  const img = image || `${SITE}/og-default.png`;
  const full = title ? `${title} — Verix` : "Verix — Verify every signup. Block every fraud.";
  return (
    <Helmet>
      <title>{full}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={full} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={full} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}

export const ORG_LD = {
  "@context": "https://schema.org", "@type": "Organization", name: "Verix",
  url: SITE, description: "Real-time email, phone and IP verification and fraud prevention.",
  sameAs: [],
};
